import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Typography from '@mui/material/Typography'
import { hashHistory } from 'react-router'
import { hasAuthourity } from '../../../../../lib/access.es6'
import { getCollectionsByPolicy } from '../../../../../lib/requests.es6'
import {
  attemptStatusMeta,
  channelLabel,
  failureText,
  formatDateTime,
  formatMoney,
  orderPath
} from '../../../../../lib/collections.es6'

/**
 * Where this policy stands in Collections: its debit order, whether collection is paused, what happens next
 * and how the last attempt went, with a link to the full history. Nothing is shown to people who cannot view
 * Collections, and a failure to load never gets in the way of the policy page.
 */
export default function PolicyCollections({ policyGuid }) {
  const [state, setState] = useState({ loading: true, items: [], error: false })

  useEffect(() => {
    if (!policyGuid || !hasAuthourity('VIEW_COLLECTIONS')) return undefined
    let cancelled = false
    setState({ loading: true, items: [], error: false })
    getCollectionsByPolicy(policyGuid).then(
      response => {
        if (!cancelled)
          setState({
            loading: false,
            items: response.data.items || [],
            error: false
          })
      },
      () => {
        if (!cancelled) setState({ loading: false, items: [], error: true })
      }
    )
    return () => {
      cancelled = true
    }
  }, [policyGuid])

  if (!policyGuid || !hasAuthourity('VIEW_COLLECTIONS')) return null
  if (state.loading) return null

  return (
    <Box
      sx={{
        background: '#FFFFFF',
        borderRadius: '8px',
        marginTop: '30px',
        padding: '20px 30px'
      }}
    >
      <Typography variant="overline" color="text.secondary">
        Collections
      </Typography>
      {state.error ? (
        <Typography variant="body2" color="text.secondary">
          Collections could not be loaded right now.
        </Typography>
      ) : state.items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          This policy has no debit order, so nothing is collected automatically.
        </Typography>
      ) : (
        state.items.map(o => {
          const last = o.last_attempt
          const meta = last ? attemptStatusMeta(last.status) : null
          return (
            <Box
              key={o.debit_order_guid}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1,
                py: 1
              }}
            >
              <Box>
                <Typography variant="body1">
                  {channelLabel(o.channel)} debit order:{' '}
                  {formatMoney(o.amount_in_cents)}
                  {o.granularity
                    ? ` every ${String(o.granularity).toLowerCase()}`
                    : ''}
                  {o.day ? ` on day ${o.day}` : ''}{' '}
                  <Chip
                    size="small"
                    label={o.held ? 'Paused' : o.status}
                    color={
                      o.held
                        ? 'warning'
                        : o.status === 'Active'
                        ? 'success'
                        : 'default'
                    }
                    variant={
                      o.held || o.status === 'Active' ? 'filled' : 'outlined'
                    }
                  />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {o.held ? `Paused: ${o.held.reason}. ` : ''}
                  {last
                    ? `Last attempt: ${meta.label}${
                        last.status === 'Failed'
                          ? ` (${failureText(
                              last.failure_category,
                              last.failure_reason
                            )})`
                          : ''
                      }. `
                    : 'No attempts yet. '}
                  {o.next_scheduled_collection_at
                    ? `Collection scheduled ${formatDateTime(
                        o.next_scheduled_collection_at
                      )}. `
                    : ''}
                  {o.next_retry_at
                    ? `Next retry ${formatDateTime(o.next_retry_at)}.`
                    : ''}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => hashHistory.push(orderPath(o.debit_order_guid))}
              >
                Open in Collections
              </Button>
            </Box>
          )
        })
      )}
    </Box>
  )
}
